import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminService,
  type AdminActivityListRow,
  type AdminHouseListRow,
} from "@/services/adminService";
import { Plus } from "lucide-react";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { rateFromPercentInput } from "@/lib/commissionRate";
import { Button, ConfirmModal } from "@/components/ui";
import { LoadingState } from "@/components/common";
import {
  DataTable,
  DataTableTitle,
  buildDataTableLabels,
} from "@/components/table";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import type {
  SalesAgentActivityAssignmentRow,
  SalesAgentHouseAssignmentRow,
} from "@/types";
import { AssignmentModal } from "./AssignmentModal";
import {
  buildAssignmentColumns,
  type AssignmentKind,
  type PendingRemove,
} from "./assignmentColumns";

interface SalesAgentAssignmentsSectionProps {
  salesAgentId: string;
}

type ActivitySortColumn = "activity" | "owner" | "rate";
type HouseSortColumn = "house" | "host" | "rate";

export function SalesAgentAssignmentsSection({
  salesAgentId,
}: SalesAgentAssignmentsSectionProps) {
  const { t } = useTranslation("salesAgent");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const tableLabels = buildDataTableLabels(tCommon);
  const queryClient = useQueryClient();

  const [addActivityOpen, setAddActivityOpen] = useState(false);
  const [addHouseOpen, setAddHouseOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(
    null,
  );

  const scopedQueryKey = [
    "admin",
    "sales-agents",
    salesAgentId,
    "scoped-assignments",
  ] as const;

  const { data: scoped, isLoading: loadingScoped } = useQuery({
    queryKey: scopedQueryKey,
    queryFn: () => adminService.getSalesAgentScopedAssignments(salesAgentId),
    enabled: !!salesAgentId,
  });

  const assignedActivityIds = useMemo(
    () => new Set(scoped?.activities.map((row) => row.activity_id) ?? []),
    [scoped?.activities],
  );

  const assignedHouseIds = useMemo(
    () => new Set(scoped?.houses.map((row) => row.house_id) ?? []),
    [scoped?.houses],
  );

  const invalidateScoped = () => {
    void queryClient.invalidateQueries({ queryKey: scopedQueryKey });
    void queryClient.invalidateQueries({ queryKey: ["admin", "sales-agents"] });
    void queryClient.invalidateQueries({
      queryKey: ["admin", "sales-agents", salesAgentId],
    });
  };

  const removeMutation = useMutation({
    mutationFn: async ({ kind, id }: PendingRemove) => {
      if (kind === "activity") {
        await adminService.deleteSalesAgentActivityAssignment(id);
      } else {
        await adminService.deleteSalesAgentHouseAssignment(id);
      }
    },
    onSuccess: () => {
      toast.success(t("scopeRemoved"));
      setPendingRemove(null);
      invalidateScoped();
    },
    onError: (err) => toastApiError(err, "generic", t("removeScopeError")),
  });

  const upsertMutation = useMutation({
    mutationFn: async ({
      kind,
      entityId,
      commissionPct,
    }: {
      kind: AssignmentKind;
      entityId: string;
      commissionPct: string;
    }) => {
      const rate = rateFromPercentInput(commissionPct.trim());
      if (rate == null) {
        throw new Error("invalid_rate");
      }
      if (kind === "activity") {
        await adminService.upsertSalesAgentActivityAssignment({
          activity_id: entityId,
          sales_agent_user_id: salesAgentId,
          commission_rate: rate,
        });
      } else {
        await adminService.upsertSalesAgentHouseAssignment({
          house_id: entityId,
          sales_agent_user_id: salesAgentId,
          commission_rate: rate,
        });
      }
    },
    onSuccess: (_data, variables) => {
      toast.success(t("scopeAssigned"));
      invalidateScoped();
      if (variables.kind === "activity") {
        setAddActivityOpen(false);
      } else {
        setAddHouseOpen(false);
      }
    },
    onError: (e: unknown) => {
      if (e instanceof Error && e.message === "invalid_rate") {
        toast.error(t("detail.invalidCommission"));
        return;
      }
      toastApiError(e);
    },
  });

  const activityColumns = useMemo(
    () =>
      buildAssignmentColumns<
        SalesAgentActivityAssignmentRow,
        ActivitySortColumn
      >({
        entityHeader: t("detail.colActivity"),
        ownerHeader: t("detail.colOwner"),
        rateHeader: t("detail.colSaRate"),
        removeLabel: t("detail.remove"),
        entitySortKey: "activity",
        ownerSortKey: "owner",
        getEntityId: (row) => row.activity_id,
        getEntityName: (row) => row.activity_name,
        getOwnerId: (row) => row.owner_user_id,
        getOwnerName: (row) => row.owner_name,
        getRate: (row) => row.commission_rate,
        removeKind: "activity",
        pendingRemove,
        isRemovePending: removeMutation.isPending,
        onRequestRemove: (id) => setPendingRemove({ kind: "activity", id }),
      }),
    [pendingRemove, removeMutation.isPending, t],
  );

  const houseColumns = useMemo(
    () =>
      buildAssignmentColumns<SalesAgentHouseAssignmentRow, HouseSortColumn>({
        entityHeader: t("detail.colHouse"),
        ownerHeader: t("detail.colHost"),
        rateHeader: t("detail.colSaRate"),
        removeLabel: t("detail.remove"),
        entitySortKey: "house",
        ownerSortKey: "host",
        getEntityId: (row) => row.house_id,
        getEntityName: (row) => row.house_name,
        getOwnerId: (row) => row.host_user_id,
        getOwnerName: (row) => row.host_name,
        getRate: (row) => row.commission_rate,
        removeKind: "house",
        pendingRemove,
        isRemovePending: removeMutation.isPending,
        onRequestRemove: (id) => setPendingRemove({ kind: "house", id }),
      }),
    [pendingRemove, removeMutation.isPending, t],
  );

  const upsertPendingKind =
    upsertMutation.isPending && upsertMutation.variables
      ? upsertMutation.variables.kind
      : null;

  return (
    <div className="space-y-5">
      <p className="text-surface-500 text-sm">{t("detail.scopedHelp")}</p>

      {loadingScoped ? <LoadingState className="py-8" /> : null}

      {!loadingScoped && scoped ? (
        <>
          <DataTable
            labels={tableLabels}
            closeLabel={tCommon("close")}
            loadingLabel={tCommon("loading")}
            title={
              <DataTableTitle count={scoped.activities.length}>
                {t("detail.activitySection")}
              </DataTableTitle>
            }
            actions={
              <Button
                type="button"
                size="sm"
                onClick={() => setAddActivityOpen(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                {t("detail.addActivity")}
              </Button>
            }
            rows={scoped.activities}
            columns={activityColumns}
            getRowKey={(row) => row.activity_id}
            defaultSortColumn="activity"
            preferencesKey="sales-agent-assignments-activities"
            paginate={false}
            emptyMessage={t("detail.emptyActivities")}
          />

          <DataTable
            labels={tableLabels}
            closeLabel={tCommon("close")}
            loadingLabel={tCommon("loading")}
            title={
              <DataTableTitle count={scoped.houses.length}>
                {t("detail.houseSection")}
              </DataTableTitle>
            }
            actions={
              <Button
                type="button"
                size="sm"
                onClick={() => setAddHouseOpen(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                {t("detail.addHouse")}
              </Button>
            }
            rows={scoped.houses}
            columns={houseColumns}
            getRowKey={(row) => row.house_id}
            defaultSortColumn="house"
            preferencesKey="sales-agent-assignments-houses"
            paginate={false}
            emptyMessage={t("detail.emptyHouses")}
          />
        </>
      ) : null}

      {addActivityOpen ? (
        <AssignmentModal
          assignedIds={assignedActivityIds}
          isPending={upsertPendingKind === "activity"}
          cancelLabel={tCommon("cancel")}
          saveLabel={tCommon("save")}
          onClose={() => setAddActivityOpen(false)}
          onSave={(entityId, commissionPct) =>
            upsertMutation.mutate({
              kind: "activity",
              entityId,
              commissionPct,
            })
          }
          queryKey={["admin", "activities", "assignment-picker"]}
          fetchItems={(search) =>
            adminService.getActivities({ search: search || undefined })
          }
          getItemId={(item: AdminActivityListRow) => item.id}
          getTitle={(item, language) =>
            resolveCatalogTranslation(item.name_translations, language)
          }
          getSubtitle={(item) => item.owner_name}
          labels={{
            title: t("detail.modalActivityTitle"),
            description: t("detail.modalActivitySubtitle"),
            stepLabel: t("detail.selectActivityStep"),
            searchPlaceholder: t("detail.searchActivitiesPlaceholder"),
            emptyMessage: t("detail.noActivitiesFound"),
            loadErrorTitle: tErrors("loadActivities"),
            commissionLabel: t("detail.saCommissionPercentLabel"),
          }}
        />
      ) : null}

      {addHouseOpen ? (
        <AssignmentModal
          assignedIds={assignedHouseIds}
          isPending={upsertPendingKind === "house"}
          cancelLabel={tCommon("cancel")}
          saveLabel={tCommon("save")}
          onClose={() => setAddHouseOpen(false)}
          onSave={(entityId, commissionPct) =>
            upsertMutation.mutate({
              kind: "house",
              entityId,
              commissionPct,
            })
          }
          queryKey={["admin", "houses", "assignment-picker"]}
          fetchItems={(search) =>
            adminService.getHouses({ search: search || undefined })
          }
          getItemId={(item: AdminHouseListRow) => item.id}
          getTitle={(item) => item.name}
          getSubtitle={(item) => item.owner_name}
          labels={{
            title: t("detail.modalHouseTitle"),
            description: t("detail.modalHouseSubtitle"),
            stepLabel: t("detail.selectStructureStep"),
            searchPlaceholder: t("detail.searchStructuresPlaceholder"),
            emptyMessage: t("detail.noStructuresFound"),
            loadErrorTitle: tErrors("loadHouses"),
            commissionLabel: t("detail.saCommissionPercentLabel"),
          }}
        />
      ) : null}

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={pendingRemove != null}
        onClose={() => {
          if (removeMutation.isPending) return;
          setPendingRemove(null);
        }}
        onConfirm={() => {
          if (pendingRemove == null) return;
          removeMutation.mutate(pendingRemove);
        }}
        title={t("detail.remove")}
        message={
          pendingRemove?.kind === "house"
            ? t("detail.removeConfirmHouse")
            : t("detail.removeConfirmActivity")
        }
        confirmText={t("detail.remove")}
        isDestructive
        isLoading={removeMutation.isPending}
      />
    </div>
  );
}
